import { LoadingButton } from '@mui/lab'
import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Switch, TextField, Typography } from '@mui/material'
import { GreenText } from 'components'
import { useAuth } from 'context'
import * as controllers from 'controllers'
import { CompanyDto, PersonDto, personValidationSchema } from 'dtos'
import { Formik } from 'formik'
import { useEffect, useState } from 'react'
import { useDebouncedEffect } from 'utils'

interface PersonAddEditDialogProps {
  initialValues: PersonDto
  loading: boolean
  onClose: () => void
  onSave: (values: PersonDto) => void
  open: boolean
}

export default function PersonAddEditDialog({ initialValues, loading, onClose, onSave, open }: PersonAddEditDialogProps) {
  const { permissions } = useAuth()

  // In VTS2, there can only ever be one declarant person at a time. We get the declarant (if it exists,) to determine if the isDeclarant switch is enabled/disabled.
  const [declarantAlreadyExists, setDeclarantAlreadyExists] = useState<boolean>(false)
  const [isGettingDeclarant, setIsGettingDeclarant] = useState<boolean>(false)

  const [declarationOfOwnershipSignerAlreadyExists, setDeclarationOfOwnershipSignerAlreadyExists] = useState<boolean>(false)
  const [isGettingDeclarationOfOwnershipSigner, setIsGettingDeclarationOfOwnershipSigner] = useState<boolean>(false)

  const [companies, setCompanies] = useState<CompanyDto[]>([])

  useDebouncedEffect(() => {
    controllers.getCompanies({ page: 0, pageSize: 2000, order: 'asc', orderBy: 'name', statuses: ['isActive'] }).then(response => {
      setCompanies(response.value)
    })
  }, [])

  useEffect(() => {
    if (!open) return

    if (!initialValues.isDeclarant) {
      setIsGettingDeclarant(true)
      controllers
        .getDeclarantPerson()
        .then(response => {
          if (response.value) {
            setDeclarantAlreadyExists(response.value.personGuid !== initialValues.personGuid)
          } else {
            setDeclarantAlreadyExists(false)
          }
        })
        .finally(() => {
          setIsGettingDeclarant(false)
        })
    } else {
      setDeclarantAlreadyExists(false)
    }

    if (!initialValues.isDeclarationOfOwnershipSigner) {
      setIsGettingDeclarationOfOwnershipSigner(true)
      controllers
        .getDeclarationOfOwnershipSignerPerson()
        .then(res => {
          if (res.value) {
            console.log(initialValues)
            console.log(initialValues.personGuid)
            console.log(res.value.personGuid)
            setDeclarationOfOwnershipSignerAlreadyExists(res.value.personGuid !== initialValues.personGuid)
          } else {
            setDeclarationOfOwnershipSignerAlreadyExists(false)
          }
        })
        .finally(() => setIsGettingDeclarationOfOwnershipSigner(false))
    } else {
      setDeclarationOfOwnershipSignerAlreadyExists(false)
    }
  }, [open, initialValues])

  return (
    <Dialog open={open} onClose={onClose}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur
        validateOnChange
        validationSchema={personValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>{values.personGuid ? 'Edit' : 'Add'} Person</DialogTitle>

                  <Grid item xs={12} container spacing={1} alignItems='center'>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        disabled={loading || isSubmitting}
                        error={Boolean(touched.firstName && errors.firstName)}
                        fullWidth
                        helperText={touched.firstName && errors.firstName}
                        label='First Name'
                        name='firstName'
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.firstName || ''}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        disabled={loading || isSubmitting}
                        error={Boolean(touched.lastName && errors.lastName)}
                        fullWidth
                        helperText={touched.lastName && errors.lastName}
                        label='Last Name'
                        name='lastName'
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.lastName || ''}
                      />
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      disabled={loading || isSubmitting}
                      disableListWrap
                      getOptionLabel={option => option.name}
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('company', value)
                      }}
                      options={companies.map(company => ({
                        companyGuid: company.companyGuid,
                        name: company.name!,
                        // isInboundTransporterCompany and isOutboundTransporterCompany are required here for the
                        // isDriver front end validation schema.
                        // Maxwell Sommer 7/25/2024
                        isInboundTransporterCompany: company.isInboundTransporterCompany,
                        isOutboundTransporterCompany: company.isOutboundTransporterCompany
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.company && errors.company)}
                          helperText={touched.company && errors.company}
                          label='Company'
                          name='company'
                        />
                      )}
                      value={
                        values.company
                          ? {
                              companyGuid: values.company.companyGuid,
                              name: values.company.name!,
                              isInboundTransporterCompany: values.company.isInboundTransporterCompany,
                              isOutboundTransporterCompany: values.company.isOutboundTransporterCompany
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.title && errors.title)}
                      fullWidth
                      helperText={touched.title && errors.title}
                      label='Title'
                      name='title'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.title || ''}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.email && errors.email)}
                      fullWidth
                      helperText={touched.email && errors.email}
                      label='Email'
                      name='email'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.email || ''}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.mobilePhone && errors.mobilePhone)}
                      fullWidth
                      helperText={touched.mobilePhone && errors.mobilePhone}
                      label='Mobile Phone'
                      name='mobilePhone'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.mobilePhone || ''}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.officePhone && errors.officePhone)}
                      fullWidth
                      helperText={touched.officePhone && errors.officePhone}
                      label='Office Phone'
                      name='officePhone'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.officePhone || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.notes && errors.notes)}
                      fullWidth
                      helperText={touched.notes && errors.notes}
                      label='Notes'
                      maxRows={4}
                      minRows={4}
                      multiline
                      name='notes'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.notes || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isActive}
                          disabled={loading || isSubmitting}
                          name='isActive'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isActive}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Active</Typography>
                          <Typography variant='body2' color='#808080'>
                            Makes this person active. If this toggle is turned off, “Access to VTS2” will also be toggled off.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isDriver}
                          disabled={loading || isSubmitting}
                          name='isDriver'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isDriver}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Driver</Typography>
                          <Typography variant='body2' color='#808080'>
                            Name of person who will appear in the “Driver” dropdowns.
                          </Typography>
                          {touched.isDriver && errors.isDriver && (
                            <Typography color='error' variant='body2'>
                              {errors.isDriver}
                            </Typography>
                          )}
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isDeclarant}
                          disabled={loading || isSubmitting || isGettingDeclarant || declarantAlreadyExists}
                          name='isDeclarant'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isDeclarant}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Declarant</Typography>
                          <Typography variant='body2' color='#808080'>
                            Name of person who will appear in the “Declarant” fields in system-generated documents (i.e., Pro Forma).
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isDeclarationOfOwnershipSigner}
                          disabled={loading || isSubmitting || isGettingDeclarationOfOwnershipSigner || declarationOfOwnershipSignerAlreadyExists}
                          name='isDeclarationOfOwnershipSigner'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isDeclarationOfOwnershipSigner}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Declaration of Ownership Signer</Typography>
                          <Typography variant='body2' color='#808080'>
                            Name of the person whose name and signature will appear on the Declaration of Ownership form included in the Send to Broker
                            documents generated for inbound trips.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isEmployee}
                          disabled={loading || isSubmitting}
                          name='isEmployee'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isEmployee}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Employee</Typography>
                          <Typography variant='body2' color='#808080'>
                            Blue Water Importers employee.{' '}
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isTitleClerk}
                          disabled={loading || isSubmitting}
                          name='isTitleClerk'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isTitleClerk}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Title Clerk</Typography>
                          <Typography variant='body2' color='#808080'>
                            Used for Title Application Shipping Labels.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.allowUserToSignIn}
                          disabled={loading || isSubmitting || !permissions.USERS_ADD_EDIT}
                          name='allowUserToSignIn'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.allowUserToSignIn}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Access to VTS2</Typography>
                          <Typography variant='body2' color='#808080'>
                            Allows user to sign into system.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**First Name:**

A required free-text field.

**Last Name:**

A required free-text field.

**Company Drop-Down:**

A Drop-down that shows all active companies within VTS2

**Title:**

A required free-text field.

**Email:**

A required free-text field.

Email must be unique system-wide among active and inactive people.

**Mobile Phone:**

A required numeric-only free-text field.

**Office Phone:**

An optional numeric-only free-text field.

**Notes:**

An optional free-text field.

**Active Switch:**

A Toggle to activate or deactivate the user.

**Driver:**

The name of the person who will appear in the “Driver” dropdowns.

**Declarant:**

The name of the person who will appear in the “Declarant” fields of system-
generated documents (e.g., Pro Forma).

**Declaration of Ownership Signer:**

The name of the person who will appear on the “Statement of Ownership” document generated for inbound trips. There can only be one person with the “Declaration of Ownership Signer” toggle activated in VTS2.

**Employee:**

Indicates if the user is a Blue Water Importers employee.

**Title Clerk:**

Indicates if the user is assigned to handle Title Application Shipping Labels.

**Access to VTS2:**

Allows the user to sign into the VTS2 system.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes

**SAVE Button:**

Clicking the SAVE button will save any changes.
`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' disabled={loading || isSubmitting} onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton loading={loading || isSubmitting} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
